
import React, { useEffect, useRef, useState, useImperativeHandle, forwardRef, useMemo, useCallback } from 'react';
import * as d3 from 'd3';
import { MindMapNode, MindMapProps, MindMapHandle, ColorPalette } from '../types';
import { getEmoji } from '../utils/emojiMap';
import { getContentColor, getContrastColor } from '../utils/colorMap';
import { PlusCircle, Trash2, AlertTriangle, Pencil, Copy, FileText, Check } from 'lucide-react';
import { translations } from '../utils/translations';

import { getFontSize, getPadding, shouldShowText } from './MindMap/utils';
import { useMindMapEvents } from '../hooks/useMindMapEvents';
import { useMindMapLayout } from '../hooks/useMindMapLayout';

const MANDALA_RADIUS_GAP = 140;
import { NodeEditor } from './MindMap/NodeEditor';
import { DescriptionEditor } from './MindMap/DescriptionEditor';

const MindMap = forwardRef<MindMapHandle, MindMapProps>(({ 
  data, 
  palette, 
  fileName,
  language,
  onNodeClick, 
  onNodeMove,
  onBulkNodeMove,
  onToggleCollapse,
  onUpdateNodeName,
  onUpdateNodeDescription,
  onReparentNode,
  onAddNode,
  onAddFloatingNode,
  onDeleteNode,
  onNodeAddedProcessed,
  selectedNodeId,
  rearrangeTrigger,
  lastAddedNodeId,
  showEmoji,
  isMandalaMode,
  initialViewState,
  isReplaying
}, ref) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [isInitialized, setIsInitialized] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingDescId, setEditingDescId] = useState<string | null>(null);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; nodeId: string } | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<{ nodeId: string, nodeName: string, hasChildren: boolean } | null>(null);
  const [descDialog, setDescDialog] = useState<{ nodeId: string, initialDesc: string } | null>(null);
  
  const t = translations[language];

  const dragStartPosRef = useRef<{x: number, y: number} | null>(null);
  const isDraggingRef = useRef(false);
  const lastClickTimeRef = useRef<number>(0);
  
  const currentTransform = useRef<d3.ZoomTransform | null>(null);
  const currentNodeScale = useRef<number>(1);
  const zoomBehaviorRef = useRef<d3.ZoomBehavior<SVGSVGElement, unknown> | null>(null);
  
  const nodePositionsRef = useRef<any[]>([]); 
  const measureTextRef = useRef<HTMLCanvasElement | null>(null);
  const justEditedRef = useRef(false); 
  const lastEditedNodeIdRef = useRef<string | null>(null);
  
  const potentialParentRef = useRef<string | null>(null);

  // Track selected node ID in a ref to use in D3 event handlers without stale closures
  const selectedNodeIdRef = useRef(selectedNodeId);
  useEffect(() => { selectedNodeIdRef.current = selectedNodeId; }, [selectedNodeId]);

  const isReplayingRef = useRef(isReplaying);
  useEffect(() => { isReplayingRef.current = isReplaying; }, [isReplaying]);

  const handlersRef = useRef({ onNodeClick, onNodeMove, onReparentNode, onToggleCollapse, onBulkNodeMove, onAddNode, onAddFloatingNode });
  useEffect(() => {
      handlersRef.current = { onNodeClick, onNodeMove, onReparentNode, onToggleCollapse, onBulkNodeMove, onAddNode, onAddFloatingNode };
  }, [onNodeClick, onNodeMove, onReparentNode, onToggleCollapse, onBulkNodeMove, onAddNode, onAddFloatingNode]);

  useImperativeHandle(ref, () => ({
    exportImage: (exportName: string) => {
        if (!svgRef.current) return;
        const svg = d3.select(svgRef.current);
        const contentLayer = svg.select("g.content-layer").node() as SVGGElement;
        if (!contentLayer) return;
        const bbox = contentLayer.getBBox();
        if (bbox.width === 0 || bbox.height === 0) { alert("Mind map is empty."); return; }
        const t = currentTransform.current || d3.zoomIdentity;
        const k = t.k;
        const clone = contentLayer.cloneNode(true) as SVGGElement;
        clone.setAttribute("transform", `scale(${k})`);
        const padding = 50; 
        const width = bbox.width * k + (padding * 2);
        const height = bbox.height * k + (padding * 2);
        const vbX = (bbox.x * k) - padding;
        const vbY = (bbox.y * k) - padding;
        const targetWidth = 2400; 
        const targetHeight = targetWidth * (height / width);
        const defsElement = svg.select("defs").node();
        const defsStr = defsElement ? new XMLSerializer().serializeToString(defsElement as Node) : "";
        const contentStr = new XMLSerializer().serializeToString(clone);
        const svgString = `<svg xmlns="http://www.w3.org/2000/svg" width="${targetWidth}" height="${targetHeight}" viewBox="${vbX} ${vbY} ${width} ${height}"><style>text { font-family: 'Inter', sans-serif; } .node-rect { transition: none; vector-effect: non-scaling-stroke; } .link { vector-effect: non-scaling-stroke; } .float-group-bg { vector-effect: non-scaling-stroke; }</style>${defsStr}${contentStr}</svg>`;
        const blob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement("canvas");
            canvas.width = targetWidth;
            canvas.height = targetHeight;
            const ctx = canvas.getContext("2d");
            if (ctx) {
                ctx.fillStyle = palette.background;
                ctx.fillRect(0, 0, targetWidth, targetHeight);
                ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
                try {
                    const webpUrl = canvas.toDataURL("image/webp", 0.9);
                    const a = document.createElement("a");
                    a.href = webpUrl;
                    a.download = `${exportName}.webp`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                } catch (e) { console.error("Export failed", e); }
            }
            URL.revokeObjectURL(url);
        };
        img.src = url;
    },
    getViewState: () => {
        if (currentTransform.current) {
            return { x: currentTransform.current.x, y: currentTransform.current.y, k: currentTransform.current.k };
        }
        return undefined;
    }
  }));

  // Handle Resize
  useEffect(() => {
    if (typeof document !== 'undefined') measureTextRef.current = document.createElement('canvas');
    
    if (!wrapperRef.current) return;
    
    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const { width, height } = entry.contentRect;
        if (width > 0 && height > 0) {
            setDimensions({ width, height });
        }
      }
    });
    
    observer.observe(wrapperRef.current);
    
    return () => observer.disconnect();
  }, []);

  // Global Event Listeners
  useEffect(() => {
      const handleClick = () => setContextMenu(null);
      window.addEventListener('click', handleClick);
      return () => window.removeEventListener('click', handleClick);
  }, []);

  const { handleDeleteClick, handleEditDescription } = useMindMapEvents(
    data, selectedNodeId, lastAddedNodeId, onAddNode, onDeleteNode, onNodeAddedProcessed,
    setEditingId, setContextMenu, setDeleteDialog, setDescDialog
  );

  // --- LAYOUT CALCULATION ---
  const layoutData = useMindMapLayout(data, dimensions, palette, isMandalaMode, showEmoji, measureTextRef); 

  const getNodeAnchors = (node: any) => { 
      if (!node._cartesian || !node._dims) return { left: {x:0,y:0}, right: {x:0,y:0}, top: {x:0,y:0}, bottom: {x:0,y:0}, center: {x:0,y:0} };
      const x = node._cartesian.x; const y = node._cartesian.y; const w = node._dims.contentWidth; const h = node._dims.height;
      const hTitle = node._dims.titleHeight || node._dims.height;
      const yOffset = (node._dims.height - hTitle) / 2; 
      const yCenter = y - yOffset;
      
      return { 
          left: { x: x - w/2, y: yCenter }, 
          right: { x: x + w/2, y: yCenter }, 
          top: { x: x, y: y - h/2 }, 
          bottom: { x: x, y: y + h/2 },
          center: { x, y: yCenter }
      }; 
  };

  const getOptimalTransform = useCallback((allNodes: any[]) => {
      const defaultScale = dimensions.width < 768 ? 0.4 : 0.85;
      const defaultT = d3.zoomIdentity.translate(dimensions.width/2, dimensions.height/2).scale(defaultScale);
      
      if (!allNodes || allNodes.length === 0) return defaultT;
      
      const rootNode = allNodes.find((n: any) => n.data.id === 'root');
      if (!rootNode) return defaultT;
      
      const rootX = rootNode._cartesian?.x || 0;
      const rootY = rootNode._cartesian?.y || 0;
      
      let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
      
      allNodes.forEach((n: any) => {
          if (n.data.isDummy || !n._cartesian || !n._dims) return;
          const x = n._cartesian.x;
          const y = n._cartesian.y;
          const w = n._dims.contentWidth || 100;
          const h = n._dims.height || 40;
          
          minX = Math.min(minX, x - w/2);
          maxX = Math.max(maxX, x + w/2);
          minY = Math.min(minY, y - h/2);
          maxY = Math.max(maxY, y + h/2);
      });
      
      if (minX === Infinity) return defaultT;
      
      const padding = 120;
      const maxDistX = Math.max(Math.abs(minX - rootX), Math.abs(maxX - rootX));
      const maxDistY = Math.max(Math.abs(minY - rootY), Math.abs(maxY - rootY));
      
      const requiredWidth = (maxDistX + padding) * 2;
      const requiredHeight = (maxDistY + padding) * 2;
      
      const w = dimensions.width;
      const h = dimensions.height;
      
      const scaleX = requiredWidth > 0 ? w / requiredWidth : 1;
      const scaleY = requiredHeight > 0 ? h / requiredHeight : 1;
      let optimalScale = Math.min(scaleX, scaleY);
      
      const isMobile = w < 768;
      const minScale = isMobile ? 0.6 : 0.15;
      
      optimalScale = Math.max(minScale, Math.min(optimalScale, 1.0));
      
      const tx = w / 2 - rootX * optimalScale;
      const ty = h / 2 - rootY * optimalScale;
      
      return d3.zoomIdentity.translate(tx, ty).scale(optimalScale);
  }, [dimensions]);

  const centerMap = useCallback(() => {
      if (!svgRef.current || !nodePositionsRef.current || nodePositionsRef.current.length === 0 || !zoomBehaviorRef.current) return;
      const targetT = getOptimalTransform(nodePositionsRef.current);
      d3.select(svgRef.current).transition().duration(750).call(zoomBehaviorRef.current.transform as any, targetT);
  }, [getOptimalTransform]);

  const hasCenteredRef = useRef(false);
  useEffect(() => {
      if (isInitialized && nodePositionsRef.current.length > 0 && !hasCenteredRef.current) {
          hasCenteredRef.current = true;
          setTimeout(() => centerMap(), 100);
      }
  }, [isInitialized, layoutData, centerMap]);

  const prevFileNameRef = useRef(fileName);
  useEffect(() => {
      if (prevFileNameRef.current !== fileName) {
          prevFileNameRef.current = fileName;
          hasCenteredRef.current = false;
      }
  }, [fileName]);

  // Init SVG & Zoom
  useEffect(() => {
    if (!svgRef.current || dimensions.width === 0) return;
    const svg = d3.select(svgRef.current);
    if (svg.select("g.content-layer").empty()) {
        svg.selectAll("*").remove(); 
        const defs = svg.append("defs");
        const f1 = defs.append("filter").attr("id", "elevation-1").attr("height", "200%").attr("color-interpolation-filters", "sRGB");
        f1.append("feGaussianBlur").attr("stdDeviation", 2);
        f1.append("feOffset").attr("dy", 1);
        f1.append("feFlood").attr("flood-color", "rgba(0,0,0,0.1)");
        f1.append("feComposite").attr("in2", "SourceAlpha").attr("operator", "in");
        const m1 = f1.append("feMerge"); m1.append("feMergeNode"); m1.append("feMergeNode").attr("in", "SourceGraphic");

        const f3 = defs.append("filter").attr("id", "elevation-3").attr("height", "200%").attr("color-interpolation-filters", "sRGB");
        f3.append("feGaussianBlur").attr("stdDeviation", 4);
        f3.append("feOffset").attr("dy", 4);
        f3.append("feFlood").attr("flood-color", "rgba(0,0,0,0.2)");
        f3.append("feComposite").attr("in2", "SourceAlpha").attr("operator", "in");
        const m3 = f3.append("feMerge"); m3.append("feMergeNode"); m3.append("feMergeNode").attr("in", "SourceGraphic");

        const glow = defs.append("filter").attr("id", "glow-blur").attr("height", "200%").attr("color-interpolation-filters", "sRGB");
        glow.append("feGaussianBlur").attr("stdDeviation", 4);
        glow.append("feMerge").selectAll("feMergeNode").data(["", "SourceGraphic"]).enter().append("feMergeNode").attr("in", d=>d||undefined);

        svg.append("rect").attr("class", "zoom-rect").attr("width", "100%").attr("height", "100%").attr("fill", "transparent");
        const g = svg.append("g").attr("class", "content-layer");
        g.append("g").attr("class", "mandala-rings"); 
        g.append("g").attr("class", "floating-groups");
        g.append("g").attr("class", "links");
        g.append("g").attr("class", "preview-layer").append("path").attr("class", "connection-preview-link").attr("vector-effect", "non-scaling-stroke");
        g.append("g").attr("class", "nodes");
        
        const zoom = d3.zoom<SVGSVGElement, unknown>().scaleExtent([0.1, 10])
          .on("start", () => { if (document.activeElement instanceof HTMLElement) document.activeElement.blur(); setEditingId(null); setEditingDescId(null); })
          .on("zoom", (e) => { 
              const t = e.transform;
              currentTransform.current = t;
              g.attr("transform", t);
              setContextMenu(null);
              
              const nodeScale = t.k > 1 ? 1 / t.k : 1;
              currentNodeScale.current = nodeScale;

              g.selectAll("g.node").attr("transform", (d: any) => 
                  `translate(${d._cartesian.x},${d._cartesian.y}) scale(${nodeScale})`
              );
              
              const effectiveScale = t.k * nodeScale;
              g.selectAll("g.node text").style("opacity", (d: any) => shouldShowText(d, effectiveScale) ? 1 : 0);
              g.selectAll("g.node .desc-group").style("opacity", (d: any) => shouldShowText(d, effectiveScale) ? 1 : 0);
          })
          .on("end", (e) => {
              if (!nodePositionsRef.current.length || !svgRef.current) return;
              const t = e.transform;
              const { width, height } = svgRef.current.getBoundingClientRect();
              
              let isVisible = false;
              for (const node of nodePositionsRef.current) {
                   const x = node._cartesian.x * t.k + t.x;
                   const y = node._cartesian.y * t.k + t.y;
                   if (x > -100 && x < width + 100 && y > -100 && y < height + 100) {
                       isVisible = true;
                       break;
                   }
              }
              
              if (!isVisible) {
                   centerMap();
              }
          })
          .filter((e) => e.type !== 'dblclick' && !e.button);
          
        zoomBehaviorRef.current = zoom;
        
        svg.call(zoom).on("dblclick.zoom", null);
        
        let t = getOptimalTransform(layoutData.allNodes);
        svg.call(zoom.transform, t); 
        currentTransform.current = t;

        svg.select(".zoom-rect")
            .on("click", () => { 
                if(document.activeElement instanceof HTMLElement) document.activeElement.blur(); 
                if (isReplayingRef.current) return;
                handlersRef.current.onNodeClick(null); 
                setContextMenu(null); 
                setEditingId(null);
                setEditingDescId(null);
            })
            .on("dblclick", (e) => {
                e.preventDefault();
                if (isReplayingRef.current) return;
                const [mx, my] = d3.pointer(e);
                const t = currentTransform.current || d3.zoomIdentity;
                const x = (mx - t.x) / t.k;
                const y = (my - t.y) / t.k;
                handlersRef.current.onAddFloatingNode(x, y);
            });

        setIsInitialized(true);
    }
  }, [dimensions.width, dimensions.height]); 

  // Render Effect
  useEffect(() => {
    if (!svgRef.current || !isInitialized) return;
    nodePositionsRef.current = layoutData.allNodes;
    const svg = d3.select(svgRef.current);
    
    // Mandala Rings
    const mandalaG = svg.select("g.mandala-rings");
    if (isMandalaMode && layoutData.mandalaRings.length > 0) {
        mandalaG.attr("transform", `scale(1, 0.65)`);
        
        const ringsData = layoutData.mandalaRings.filter((r: any) => r.nodes.length > 0);
        const ringSel = mandalaG.selectAll("g.ring-group").data(ringsData, (d: any) => d.depth);
        
        ringSel.exit().transition().duration(400).attr("opacity", 0).remove();
        
        const baseOpacity = palette.type === 'dark' ? 0.6 : 0.3;
        
        const ringEnter = ringSel.enter().append("g").attr("class", "ring-group").attr("opacity", 0);
        
        const ringUpdate = ringEnter.merge(ringSel as any);
        
        ringUpdate.each(function(ring: any) {
             const layer = d3.select(this);
             layer.selectAll("*").remove(); // Rebuild paths for the ring
             const { depth, nodes: nodesAtDepth } = ring;
             nodesAtDepth.forEach((node: any, i: number) => {
                 const nextNode = nodesAtDepth[(i + 1) % nodesAtDepth.length];
                 let startAngle = node.x;
                 let endAngle = nextNode.x;
                 if (endAngle < startAngle) endAngle += 2 * Math.PI;
                 const colorInterpolator = d3.interpolateRgb(node._color, nextNode._color);
                 const steps = 64;
                 const angleStep = (endAngle - startAngle) / steps;
                 for (let j = 0; j < steps; j++) {
                     const a1 = startAngle + j * angleStep;
                     const a2 = startAngle + (j + 1) * angleStep + 0.01;
                     const gap = (layoutData as any).mandalaRadiusGap || MANDALA_RADIUS_GAP;
                     const arcGen = d3.arc()
                        .innerRadius(depth * gap - gap / 2 + 2)
                        .outerRadius(depth * gap + gap / 2 + 2)
                        .startAngle(a1)
                        .endAngle(a2);
                     layer.append("path").attr("d", arcGen as any).attr("fill", colorInterpolator(j / steps)).attr("fill-opacity", 1).style("pointer-events", "none"); 
                 }
             });
        });
        
        ringEnter.transition().duration(800).delay((d: any, i: number) => i * 200).attr("opacity", baseOpacity);
        ringSel.transition().duration(400).attr("opacity", baseOpacity);
        
    } else {
        mandalaG.selectAll("*").remove();
        mandalaG.attr("transform", null);
    }

    // Floating Groups
    const floatG = svg.select("g.floating-groups");
    floatG.selectAll("*").remove();
    layoutData.floatingGroups.forEach((box:any) => {
        floatG.append("ellipse").attr("cx", box.cx).attr("cy", box.cy).attr("rx", box.width/2+30).attr("ry", box.height/2+30)
            .attr("fill", "none").attr("stroke-dasharray", "4 2").style("pointer-events", "none").attr("class", "float-group-bg").attr("vector-effect", "non-scaling-stroke");
    });

    // Links
    const linkG = svg.select("g.links");
    const linkSel = linkG.selectAll("path.link").data(layoutData.links, (d:any) => `${d.source.data.id}-${d.target.data.id}`);
    
    linkSel.enter().append("path").attr("class", (d:any) => `link link-elastic link-to-${d.target.data.id}`).attr("fill", "none").attr("stroke-width", 5).attr("opacity", 0)
        .merge(linkSel as any)
        .attr("vector-effect", "non-scaling-stroke")
        // Ensure reset of styles if they were modified by drag
        .style("stroke-dasharray", "none")
        .attr("stroke-width", 5) 
        .transition().duration(500).attr("opacity", 0.8).attr("d", (d:any) => {
            if(!d.source._cartesian || !d.target._cartesian) return "";
            
            // USE CENTER ANCHORS TO FIX DETACHMENT ON ZOOM
            const source = getNodeAnchors(d.source).center;
            const target = getNodeAnchors(d.target).center;

            if((d.target._side==='top'||d.target._side==='bottom') && d.target.depth===1) return d3.linkVertical<any, {x:number,y:number}>().x(p=>p.x).y(p=>p.y)({source,target});
            return d3.linkHorizontal<any, {x:number,y:number}>().x(p=>p.x).y(p=>p.y)({source,target});
        });
    linkSel.exit().remove();

    // Nodes
    const nodeG = svg.select("g.nodes");
    const valid = layoutData.allNodes.filter((d:any) => !d.data.isDummy);
    const nodeSel = nodeG.selectAll("g.node").data(valid, (d:any) => d.data.id);
    
    const nodeEnter = nodeSel.enter().append("g").attr("class", "node node-transition").attr("cursor", "pointer").attr("opacity", 0);
    
    const rectGroup = nodeEnter.append("g").attr("class", "node-rect-group");
    rectGroup.append("rect").attr("class", "node-rect");
    
    const textGroup = nodeEnter.append("g").attr("class", "text-group").style("pointer-events", "none");
    textGroup.append("text").attr("class", "node-text").attr("text-anchor", "middle").attr("dominant-baseline", "middle");
    
    // Group for Description
    const descGroup = textGroup.append("g").attr("class", "desc-group").attr("opacity", 1);
    descGroup.append("rect").attr("class", "desc-box").attr("rx", 4).attr("ry", 4);
    descGroup.append("text").attr("class", "desc-text").attr("text-anchor", "middle").attr("dominant-baseline", "hanging");

    const nodeUpdate = nodeEnter.merge(nodeSel as any);
    
    nodeUpdate.attr("transform", (d:any) => `translate(${d._cartesian.x},${d._cartesian.y}) scale(${currentNodeScale.current})`)
              .transition().duration(500).attr("opacity", 1);
    
    nodeUpdate.on("contextmenu", (e, d) => { 
        e.preventDefault(); 
        if (isReplayingRef.current) return;
        setContextMenu({ x: e.pageX, y: e.pageY, nodeId: d.data.id }); 
    });

    nodeUpdate.call(d3.drag<any, any>()
        .subject((e, d) => ({x: d._cartesian.x, y: d._cartesian.y}))
        .filter(e => !e.button && !isReplayingRef.current)
        .on("start", function(e, d) {
            if(document.activeElement instanceof HTMLElement) document.activeElement.blur();
            isDraggingRef.current = true; dragStartPosRef.current = {x:e.x, y:e.y};
            wrapperRef.current?.classList.add("disable-transition");
            d3.select(this).classed("dragging", true);
            d3.select(this).select("g.node-rect-group").style("filter", d.data.isFloating ? "url(#elevation-3)" : "url(#elevation-1)");
            potentialParentRef.current = null;
            
            // Show parent link immediately if dragging starts
            if (d.parent) {
                 const linkLayer = d3.select(svgRef.current).select("g.links");
                 linkLayer.append("path")
                    .attr("class", `drag-link-persistent link-to-${d.data.id}`)
                    .attr("fill", "none")
                    .attr("stroke", d._color)
                    .attr("stroke-width", 5)
                    .attr("opacity", 0.8)
                    .attr("vector-effect", "non-scaling-stroke");
            }
        })
        .on("drag", function(e, d) {
            const node = d3.select(this);
            if (!node.classed("drag-raised")) { node.raise(); node.classed("drag-raised", true); }
            node.attr("transform", `translate(${e.x},${e.y}) scale(${currentNodeScale.current})`);
            
            const dropX = e.x, dropY = e.y;
            
            // Temporary object for node at drag position to calculate anchors
            const draggingNodeProxy = { ...d, _cartesian: { x: dropX, y: dropY } };
            const draggingAnchors = getNodeAnchors(draggingNodeProxy);

            const isDescendant = (p:any, cid:string):boolean => p.data.id===cid || (p.children && p.children.some((c:any)=>isDescendant(c,cid)));
            
            // Allow snapping to current parent for re-attachment/snapping
            const candidates = nodePositionsRef.current.filter((n:any) => 
                !n.data.isFloating && 
                !n.data.isDummy && 
                n.data.id !== d.data.id && 
                !isDescendant(d, n.data.id)
            );

            const SNAP_THRESHOLD = 150; 
            const MAX_STROKE = 5;

            let bestCandidate: any = null;
            let minDistance = Infinity;

            candidates.forEach((p:any) => {
                if(isMandalaMode) {
                    const searchLimit = Math.floor((Math.hypot(dropX,dropY)+60)/135);
                    if(p.depth > searchLimit) return;
                }
                
                const targetAnchors = getNodeAnchors(p);
                const pointsP = [targetAnchors.left, targetAnchors.right, targetAnchors.top, targetAnchors.bottom, targetAnchors.center];
                const pointsD = [draggingAnchors.left, draggingAnchors.right, draggingAnchors.top, draggingAnchors.bottom, draggingAnchors.center];
                
                let distBetweenAnchors = Infinity;
                pointsP.forEach(pp => {
                    pointsD.forEach(pd => {
                        const dist = Math.hypot(pp.x - pd.x, pp.y - pd.y);
                        if (dist < distBetweenAnchors) distBetweenAnchors = dist;
                    });
                });

                if(distBetweenAnchors < minDistance && distBetweenAnchors < SNAP_THRESHOLD) { 
                    minDistance = distBetweenAnchors; 
                    bestCandidate = p; 
                }
            });
            
            // 1. Calculate Strength to New Candidate
            let candidateStrength = 0; // 0 to 1
            if (bestCandidate) {
                candidateStrength = Math.max(0, 1 - minDistance / SNAP_THRESHOLD);
            }

            // 2. Calculate Strength to Current Parent
            let currentParentStrength = 0;
            const isElastic = d.parent && !d.data.isFloating; 
            
            if (isElastic) {
                const pAnchors = getNodeAnchors(d.parent);
                const pointsP = [pAnchors.left, pAnchors.right, pAnchors.top, pAnchors.bottom, pAnchors.center];
                const pointsD = [draggingAnchors.left, draggingAnchors.right, draggingAnchors.top, draggingAnchors.bottom, draggingAnchors.center];
                let distToCurrent = Infinity;
                pointsP.forEach(pp => pointsD.forEach(pd => {
                    const dist = Math.hypot(pp.x - pd.x, pp.y - pd.y);
                    if (dist < distToCurrent) distToCurrent = dist;
                }));

                currentParentStrength = Math.max(0, 1 - distToCurrent / SNAP_THRESHOLD);

                const link = d3.select(svgRef.current).select(`.link-to-${d.data.id}`);
                if (!link.empty()) {
                    const source = getNodeAnchors(d.parent).center;
                    const target = getNodeAnchors(draggingNodeProxy).center;

                    const pathGen = d3.linkHorizontal<any, {x:number, y:number}>().x(p => p.x).y(p => p.y);
                    link.attr("d", pathGen({source, target}) || "").style("opacity", 0.8);

                    const width = Math.max(0.5, currentParentStrength * MAX_STROKE);
                    link.attr("stroke-width", width);
                    
                    if ((bestCandidate && candidateStrength > currentParentStrength) || currentParentStrength === 0) {
                         link.style("stroke-dasharray", "5, 5").style("opacity", 0.3);
                    } else {
                         link.style("stroke-dasharray", "none").style("opacity", 0.8);
                    }
                }
            }

            // 3. Determine Winner
            let attachToCandidate = false;
            if (bestCandidate) {
                if (!isElastic) attachToCandidate = true;
                else if (candidateStrength > currentParentStrength) attachToCandidate = true;
                // Specifically allow re-snapping to current parent if very close
                else if (bestCandidate.data.id === d.parent.data.id && candidateStrength > 0.9) attachToCandidate = true;
            }
            
            potentialParentRef.current = attachToCandidate ? bestCandidate.data.id : null;

            // 4. Update Provisional Link
            const pv = d3.select(svgRef.current).select(".connection-preview-link");
            if (bestCandidate) {
                pv.attr("d", `M${dropX},${dropY} L${bestCandidate._cartesian.x},${bestCandidate._cartesian.y}`)
                  .attr("stroke", "#22d3ee"); 
                
                const width = Math.max(0.5, candidateStrength * MAX_STROKE);
                pv.attr("stroke-width", width).style("opacity", attachToCandidate ? 0.8 : 0.4);
            } else {
                pv.style("opacity", 0);
            }
        })
        .on("end", function(e, d) {
            isDraggingRef.current = false;
            wrapperRef.current?.classList.remove("disable-transition");
            const node = d3.select(this);
            node.classed("dragging", false).classed("drag-raised", false);
            d3.select(svgRef.current).select(".connection-preview-link").style("opacity",0);
            d3.select(svgRef.current).selectAll(`.drag-link-persistent`).remove();
            
            // Check if it was a click (mouse didn't move much)
            const clickDx = e.x - dragStartPosRef.current.x;
            const clickDy = e.y - dragStartPosRef.current.y;
            const dist = Math.sqrt(clickDx*clickDx + clickDy*clickDy);
            
            if (dist < 5) {
                // It's a click!
                const target = e.sourceEvent.target as Element;
                const isDescClick = target.closest(".desc-group");
                
                const now = Date.now();
                const isDoubleClick = lastClickTimeRef.current && (now - lastClickTimeRef.current < 300);
                lastClickTimeRef.current = now;

                if (isDoubleClick) {
                    if (isDescClick) setEditingDescId(d.data.id);
                    else setEditingId(d.data.id);
                } else {
                    if (selectedNodeIdRef.current === d.data.id) {
                        if (isDescClick) setEditingDescId(d.data.id);
                        else setEditingId(d.data.id);
                    } else {
                        if(!justEditedRef.current || lastEditedNodeIdRef.current !== d.data.id) {
                            if(e.sourceEvent.altKey || e.sourceEvent.metaKey) { 
                                handlersRef.current.onToggleCollapse(d.data.id); 
                            } else {
                                handlersRef.current.onNodeClick(d.data);
                            }
                        }
                    }
                }
                return;
            }

            // 1. Snap to potential parent (new or re-attaching current)
            if(potentialParentRef.current) { 
                const parentId = potentialParentRef.current;
                const parentNode = nodePositionsRef.current.find((n:any) => n.data.id === parentId);

                let initialOffset = undefined; // Force snapping by clearing offset
                let quadrant: 'right' | 'left' | 'top' | 'bottom' | undefined = undefined;

                if (parentNode && parentNode.depth === 0 && !isMandalaMode && !parentNode.data.isFloating) {
                     const dx = e.x - parentNode._cartesian.x;
                     const dy = e.y - parentNode._cartesian.y;
                     if (Math.abs(dx) > Math.abs(dy)) quadrant = dx > 0 ? 'right' : 'left';
                     else quadrant = dy > 0 ? 'bottom' : 'top';
                }

                handlersRef.current.onReparentNode(d.data.id, parentId, initialOffset, quadrant, undefined); 
                return; 
            }
            
            // 2. Severing Logic (detaching from parent to floating)
            const SEVER_THRESHOLD = 150;
            if(!isMandalaMode) {
                if (d.parent && !d.data.isFloating) {
                    const pA = getNodeAnchors(d.parent).center;
                    const dist = Math.hypot(pA.x - e.x, pA.y - e.y);
                    if (dist > SEVER_THRESHOLD) {
                        handlersRef.current.onReparentNode(d.data.id, 'root', { x: e.x, y: e.y });
                        return;
                    }
                }
            } else {
                const r = Math.hypot(e.x, e.y);
                const maxD = d3.max(nodePositionsRef.current.filter(n=>n._side==='mandala'), (n:any)=>n.depth)||0;
                const gap = (layoutData as any).mandalaRadiusGap || MANDALA_RADIUS_GAP;
                if(!d.data.isFloating && r > ((maxD+1)*gap + 80) && d.depth>0) {
                    handlersRef.current.onReparentNode(d.data.id, 'root', {x:e.x, y:e.y});
                    return;
                }
            }
            
            // 3. Simple position update
            const moveDx = e.x - d._cartesian.baseX;
            const moveDy = e.y - d._cartesian.baseY;
            if(dragStartPosRef.current && Math.hypot(e.x-dragStartPosRef.current.x, e.y-dragStartPosRef.current.y)>3) {
                handlersRef.current.onNodeMove(d.data.id, moveDx, moveDy);
            } else {
                d3.select(this).attr("transform", `translate(${d._cartesian.x},${d._cartesian.y}) scale(${currentNodeScale.current})`);
            }
        })
    );

    // Consolidated Text & Description Rendering
    nodeUpdate.each(function(d:any) {
        const el = d3.select(this);
        const titleHeight = d._dims.titleHeight;
        const textEl = el.select("text.node-text").style("pointer-events", "auto"); 
        textEl.selectAll("*").remove();
        
        const totalHeight = d._dims.height;
        const titleCenterY = (-totalHeight/2) + (titleHeight/2);
        const lh = getFontSize(d)*1.2;
        const startY = titleCenterY - (d._lines.length*lh/2) + lh/2 + 1;
        const emoji = getEmoji(d.data.name);
        const showIcon = showEmoji && emoji && (d.depth>0 || d.data.isFloating || d._isFloatingTree);
        
        d._lines.forEach((l:string, i:number) => {
            const t = textEl.append("tspan").attr("x", 0).attr("dy", i===0?startY:lh).text(l);
            if(i===0) t.attr("y", 0);
            if(i===0 && showIcon) { 
                t.attr("dx", 6);
                textEl.append("tspan").attr("class", "emoji-icon").attr("x", (-d._dims.textWidth/2) - 4).attr("y", startY).attr("dy", 1).text(emoji); 
            }
        });
        
        const descG = el.select("g.desc-group");
        if (d.data.description) {
            descG.style("display", "block").style("pointer-events", "auto");
            const descTopY = (-totalHeight/2) + titleHeight + (d._dims.descGap || 0) - 2; 
            descG.attr("transform", `translate(0, ${descTopY})`);
            const descBox = descG.select("rect.desc-box");
            const boxWidth = d._dims.contentWidth - 6; 
            const boxHeight = d._dims.descHeight - 4; 
            descBox.attr("x", -boxWidth/2).attr("y", 2).attr("width", boxWidth).attr("height", boxHeight)
                .attr("fill", palette.type === 'dark' ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.02)").attr("stroke", "none");
            const descText = descG.select("text.desc-text");
            descText.selectAll("*").remove();
            const dLh = 14; 
            d._descLines.forEach((l:string, i:number) => {
                descText.append("tspan").attr("x", 0).attr("dy", i===0 ? 1 + dLh/2 : dLh).text(l).style("font-size", "11px");
            });
        } else { descG.style("display", "none"); }
    });

    nodeSel.exit().transition().duration(300).attr("opacity", 0).remove();
    
    // VISUAL STYLING UPDATE
    const currentK = currentTransform.current?.k || 1;
    const effectiveK = currentK * currentNodeScale.current;
    const allNodesSel = svg.selectAll("g.node");
    
    allNodesSel.select("rect.node-rect")
      .attr("fill", (d:any) => {
           if (isMandalaMode && d.depth === 0 && !d.data.isFloating) return "transparent";
           if (!isMandalaMode && d.depth === 0 && !d.data.isFloating) return palette.nodeBackground;
           return d._color;
      })
      .attr("stroke", (d:any) => {
           if (selectedNodeId === d.data.id) return palette.type === 'dark' ? '#fff' : '#000';
           if (isMandalaMode && d.depth === 0 && !d.data.isFloating) return "none";
           if (!isMandalaMode && d.depth === 0 && !d.data.isFloating) return palette.type === 'dark' ? '#ffffff' : '#000000';
           return palette.lines;
      })
      .attr("stroke-width", (d:any) => {
          if (selectedNodeId === d.data.id) return 3;
          if (isMandalaMode && d.depth === 0 && !d.data.isFloating) return 0;
          if (d.depth === 0 && !d.data.isFloating) return 1;
          return 0.5;
      })
      .attr("stroke-opacity", (d:any) => selectedNodeId === d.data.id ? 1 : 0.5)
      .attr("width", (d:any)=>d._dims.contentWidth).attr("height", (d:any)=>d._dims.height)
      .attr("x", (d:any)=>-d._dims.contentWidth/2).attr("y", (d:any)=>-d._dims.height/2)
      .attr("rx", (d:any)=> d._dims.titleHeight ? d._dims.titleHeight/2 : d._dims.height/2).attr("ry", (d:any)=> d._dims.titleHeight ? d._dims.titleHeight/2 : d._dims.height/2);

    allNodesSel.select("g.node-rect-group")
      .style("filter", (d:any) => {
           if (isMandalaMode && d.depth === 0 && selectedNodeId !== d.data.id && !d.data.isFloating) return "none";
           if (selectedNodeId === d.data.id) return "drop-shadow(0px 0px 8px rgba(255, 255, 255, 0.8))";
           if (d.data.isFloating || d.depth === 0) return "drop-shadow(0px 4px 8px rgba(0, 0, 0, 0.2))"; 
           return "drop-shadow(0px 1px 2px rgba(0, 0, 0, 0.1))";
      });

    allNodesSel.select("text.node-text")
      .style("opacity", (d:any) => (d.data.id === editingId || !shouldShowText(d, effectiveK)) ? 0 : 1)
      .style("font-size", (d:any) => `${getFontSize(d)}px`)
      .style("font-weight", (d:any) => d.depth === 0 ? "700" : "500")
      .style("fill", (d:any) => {
           if (d.depth === 0 && !d.data.isFloating && !isMandalaMode) return getContrastColor(palette.nodeBackground);
           return getContrastColor(d.depth === 0 && !d.data.isFloating ? palette.background : d._color);
      });
      
    allNodesSel.select("text.desc-text")
       .style("fill", (d:any) => {
           if (d.depth === 0 && !d.data.isFloating && !isMandalaMode) return getContrastColor(palette.nodeBackground);
           return getContrastColor(d.depth === 0 && !d.data.isFloating ? palette.background : d._color);
       })
       .style("opacity", (d:any) => (d.data.id === editingDescId || !shouldShowText(d, effectiveK)) ? 0 : 0.9);
       
    allNodesSel.select("g.desc-group").style("opacity", (d:any) => shouldShowText(d, effectiveK) ? 1 : 0);

    svg.selectAll(".float-group-bg").style("fill", palette.type === 'light' ? "#e2e8f0" : "#334155").style("stroke", palette.lines);
    svg.selectAll("path.link").style("stroke", (d:any) => {
        if (d.target._isFloatingTree || d.target.data.isFloating) return palette.type === 'light' ? "#000" : "#fff";
        return d.target._color || palette.text;
    });

  }, [layoutData, selectedNodeId, editingId, editingDescId, palette, isMandalaMode, showEmoji, isInitialized]); 

  const renderEditor = () => {
    if (!isInitialized) return null;
    const targetId = editingId || editingDescId;
    if (!targetId) return null;
    const node = layoutData.allNodes.find((n: any) => n.data.id === targetId);
    if (!node) return null;
    const findNodeData = (root: MindMapNode): MindMapNode | null => {
        if (root.id === targetId) return root;
        if (root.children) { for (const child of root.children) { const res = findNodeData(child); if (res) return res; } }
        return null;
    };
    const freshNodeData = findNodeData(data);
    if (!freshNodeData) return null;
    const t = currentTransform.current || getOptimalTransform(layoutData.allNodes);
    if (editingId) {
        return <NodeEditor key={`editor-${editingId}`} node={node} freshNodeData={freshNodeData} transform={t} palette={palette} isMandalaMode={isMandalaMode} onSave={(val) => {
                    justEditedRef.current = true; lastEditedNodeIdRef.current = editingId;
                    setTimeout(() => { justEditedRef.current = false; lastEditedNodeIdRef.current = null; }, 200); 
                    if (val !== freshNodeData.name) onUpdateNodeName(editingId, val); setEditingId(null);
                }} onCancel={() => setEditingId(null)} nodeScale={currentNodeScale.current} />;
    } else if (editingDescId) {
        return <DescriptionEditor key={`desc-editor-${editingDescId}`} node={node} freshNodeData={freshNodeData} transform={t} palette={palette} isMandalaMode={isMandalaMode} onSave={(val) => {
                    justEditedRef.current = true; lastEditedNodeIdRef.current = editingDescId;
                    setTimeout(() => { justEditedRef.current = false; lastEditedNodeIdRef.current = null; }, 200); 
                    if (val !== freshNodeData.description && onUpdateNodeDescription) onUpdateNodeDescription(editingDescId, val); setEditingDescId(null);
                }} onCancel={() => setEditingDescId(null)} nodeScale={currentNodeScale.current} />;
    }
    return null;
  };

  return (
    <div ref={wrapperRef} className="w-full h-full overflow-hidden relative" style={{ backgroundColor: palette.background }}>
       <svg ref={svgRef} width="100%" height="100%" className="w-full h-full outline-none touch-none select-none" />
       {renderEditor()}
       {contextMenu && (
           <div className="fixed z-50 bg-white dark:bg-slate-800 shadow-lg rounded-lg border border-slate-200 dark:border-slate-700 py-1 min-w-[180px]" style={{ left: contextMenu.x, top: contextMenu.y }}>
               <button className="w-full text-left px-4 py-2 hover:bg-slate-100 dark:hover:bg-slate-700 text-sm flex items-center gap-2 text-slate-700 dark:text-slate-200" onClick={() => { setEditingId(contextMenu.nodeId); setContextMenu(null); }}><Pencil className="w-4 h-4" /> {t.editText}</button>
               <button className="w-full text-left px-4 py-2 hover:bg-slate-100 dark:hover:bg-slate-700 text-sm flex items-center gap-2 text-slate-700 dark:text-slate-200" onClick={() => handleEditDescription(contextMenu.nodeId)}><FileText className="w-4 h-4" /> {t.editDescription}</button>
               <button className="w-full text-left px-4 py-2 hover:bg-slate-100 dark:hover:bg-slate-700 text-sm flex items-center gap-2 text-slate-700 dark:text-slate-200" onClick={() => { onAddNode(contextMenu.nodeId); setContextMenu(null); }}><PlusCircle className="w-4 h-4" /> {t.insertSubTopic}</button>
               {contextMenu.nodeId !== 'root' && (<button className="w-full text-left px-4 py-2 hover:bg-red-50 dark:hover:bg-red-900/30 text-sm flex items-center gap-2 text-red-600 dark:text-red-400" onClick={() => handleDeleteClick(contextMenu.nodeId)}><Trash2 className="w-4 h-4" /> {t.delete}</button>)}
           </div>
       )}
       {descDialog && (
           <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
               <div className="bg-white dark:bg-slate-900 p-6 rounded-xl shadow-2xl max-w-sm w-full border border-slate-200 dark:border-slate-700">
                   <h3 className="text-lg font-bold mb-4 text-slate-800 dark:text-slate-100">{t.descriptionTitle}</h3>
                   <textarea className="w-full h-24 p-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-sm resize-none focus:ring-2 focus:ring-indigo-500 outline-none" placeholder={t.descriptionPlaceholder} defaultValue={descDialog.initialDesc} autoFocus
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { if (onUpdateNodeDescription) onUpdateNodeDescription(descDialog.nodeId, e.currentTarget.value); setDescDialog(null); }
                            else if (e.key === 'Escape') setDescDialog(null);
                        }}
                   />
                   <div className="flex justify-end gap-2 mt-4">
                       <button onClick={() => setDescDialog(null)} className="px-3 py-1.5 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200">{t.cancel}</button>
                       <button onClick={() => { const ta = document.querySelector('textarea'); if(ta && onUpdateNodeDescription) onUpdateNodeDescription(descDialog.nodeId, ta.value); setDescDialog(null); }} className="px-3 py-1.5 text-sm bg-indigo-500 hover:bg-indigo-600 text-white rounded-md flex items-center gap-1"><Check className="w-3 h-3" /> {t.save}</button>
                   </div>
               </div>
           </div>
       )}
       {deleteDialog && (
           <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
               <div className="bg-white dark:bg-slate-900 p-6 rounded-xl shadow-2xl max-w-md w-full border border-slate-200 dark:border-slate-700">
                   <div className="flex items-center gap-3 mb-4 text-slate-800 dark:text-slate-100"><div className="p-3 bg-red-100 dark:bg-red-900/50 rounded-full text-red-600 dark:text-red-400"><AlertTriangle className="w-6 h-6" /></div><h3 className="text-lg font-bold">{t.deleteNodeTitle}</h3></div>
                   <p className="text-slate-600 dark:text-slate-300 mb-6">{t.deleteNodeBody(deleteDialog.nodeName)}</p>
                   <div className="space-y-2">
                       <button onClick={() => { onDeleteNode(deleteDialog.nodeId, 'cascade'); setDeleteDialog(null); }} className="w-full p-3 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/40 text-left flex items-center gap-3 transition-colors group"><Trash2 className="w-5 h-5 text-red-600 dark:text-red-400" /><div><div className="font-semibold text-red-900 dark:text-red-200 text-sm">{t.deleteSubtree}</div><div className="text-xs text-red-700 dark:text-red-400/70">{t.deleteSubtreeDesc}</div></div></button>
                       <button onClick={() => { onDeleteNode(deleteDialog.nodeId, 'reparent'); setDeleteDialog(null); }} className="w-full p-3 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-left flex items-center gap-3 transition-colors"><Copy className="w-5 h-5 text-slate-500 dark:text-slate-400" /><div><div className="font-semibold text-slate-800 dark:text-slate-200 text-sm">{t.keepChildren}</div><div className="text-xs text-slate-500 dark:text-slate-400">{t.keepChildrenDesc}</div></div></button>
                   </div>
                   <div className="mt-6 flex justify-end"><button onClick={() => setDeleteDialog(null)} className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 text-sm font-medium">{t.cancel}</button></div>
               </div>
           </div>
       )}

       <button 
           onClick={centerMap}
           title="Center Map"
           className="flex items-center justify-center absolute bottom-8 md:bottom-6 right-6 p-3 bg-white dark:bg-slate-800 backdrop-blur-sm rounded-full shadow-xl border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:text-indigo-600 dark:hover:text-indigo-400 hover:scale-105 transition-all z-50 focus:outline-none"
       >
           <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"></circle><path d="M19 12h3"></path><path d="M2 12h3"></path><path d="M12 2v3"></path><path d="M12 19v3"></path></svg>
       </button>
    </div>
  );
});

export default MindMap;
